/** Pure collapsed-summary derivation for the targeting accordion — reads the
 *  DRAFT meta (so unsaved edits show) with display names resolved from refs.
 *  Rendered as chips by NotificationRulePanel; unit-tested separately. */
import type { NotificationCore, NotificationRefs } from '@/api/notifications';
import { BOOKING_STATE_LABELS } from '@/api/notifications';

/** "2d 3h" style humanization of a duration in seconds (max two units). */
export function humanizeSeconds(seconds: number): string {
  const abs = Math.abs(Math.round(seconds));
  if (abs === 0) return '0s';
  const units: [number, string][] = [
    [86400, 'd'],
    [3600, 'h'],
    [60, 'm'],
    [1, 's'],
  ];
  const parts: string[] = [];
  let rest = abs;
  for (const [size, suffix] of units) {
    const n = Math.floor(rest / size);
    if (n > 0) {
      parts.push(`${n}${suffix}`);
      rest -= n * size;
    }
    if (parts.length === 2) break;
  }
  return parts.join(' ');
}

export interface TargetingSummaryPart {
  text: string;
  tone?: 'primary' | 'warning';
}

/** Events whose delivery checks `booking_state` (dashboard parity). */
export function eventHasBookingState(event: string | undefined): boolean {
  return /^(tour_participant|order)\./.test(event ?? '');
}

export function targetingSummary(
  meta: Partial<NotificationCore>,
  refs: Pick<NotificationRefs, 'event_label' | 'roles' | 'tours'>,
): TargetingSummaryPart[] {
  const parts: TargetingSummaryPart[] = [];

  const event = meta.event ?? '';
  parts.push({ text: refs.event_label ?? event ?? '(no event)', tone: 'primary' });

  const roleNames = new Map(refs.roles.map((r) => [r.id, r.name]));
  const audience: string[] = (meta.roles ?? []).map((id) => roleNames.get(id) ?? `role #${id}`);
  if (meta.apply_to_guests) audience.push('guests');
  if (meta.apply_to_observers) audience.push('observers');
  if (audience.length) parts.push({ text: `to ${audience.join(', ')}` });

  if (eventHasBookingState(event)) {
    const state = meta.booking_state ?? 1;
    parts.push({ text: BOOKING_STATE_LABELS[state] ?? `state ${state}` });
  }

  const mode = meta.tour_override_mode ?? 1;
  if (mode === 0) parts.push({ text: 'send always' });
  if (mode === 2) {
    const count = (meta.tours ?? []).length;
    parts.push({ text: `tour-specific (${count} tour${count === 1 ? '' : 's'})` });
  }

  if ((meta.lock_delivery ?? 0) === 1) {
    parts.push({ text: `lock ${humanizeSeconds(meta.lock_time ?? 0)}` });
  }

  const tagCount = (meta.tags ?? []).length;
  if (tagCount) parts.push({ text: `${tagCount} tag${tagCount === 1 ? '' : 's'}` });
  const atagCount = (meta.atag ?? []).length;
  if (atagCount) parts.push({ text: `${atagCount} access tag${atagCount === 1 ? '' : 's'}` });

  if (meta.ignore_user_nf_prefs) parts.push({ text: 'ignores user prefs', tone: 'warning' });

  return parts;
}
